import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  FormControl,
  FormLabel,
  Switch,
  Select,
  Option,
  Input,
  Button,
  Chip,
  Divider,
} from '@mui/joy';
import { FilterList, Clear } from '@mui/icons-material';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';

export interface ScheduleFilters {
  hidePastSchedules: boolean;
  shiftTypeId: number | null;
  dateRange: {
    start: string;
    end: string;
  } | null;
  status: 'all' | 'draft' | 'published' | 'updated';
  tagIds: number[];
  userId: number | null;
  showInactiveUsersOnly: boolean;
}

interface ScheduleFiltersProps {
  shiftTypes: ShiftType[];
  onFiltersChange: (filters: ScheduleFilters) => void;
  filters: ScheduleFilters;
  tags?: Array<{ id: number; name: string; color: string }>;
  users?: User[];
  onClose?: () => void;
}

export default function ScheduleFilters({ shiftTypes, onFiltersChange, filters, tags = [], users = [], onClose }: ScheduleFiltersProps) {
  const [localFilters, setLocalFilters] = useState<ScheduleFilters>({
    ...filters,
    dateRange: filters.dateRange || { start: '', end: '' },
    tagIds: filters.tagIds || []
  });

  useEffect(() => {
    setLocalFilters({
      ...filters,
      dateRange: filters.dateRange || { start: '', end: '' },
      tagIds: filters.tagIds || []
    });
  }, [filters]);



  const handleFilterChange = (key: keyof ScheduleFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const newDateRange = localFilters.dateRange ? { ...localFilters.dateRange } : { start: '', end: '' };
    newDateRange[field] = value;
    
    // Always update the local state with the new date range, even if incomplete
    const newFilters = {
      ...localFilters,
      dateRange: newDateRange
    };
    setLocalFilters(newFilters);
  };



  const clearFilters = () => {
    const defaultFilters: ScheduleFilters = {
      hidePastSchedules: true,
      shiftTypeId: null,
      dateRange: null,
      status: 'all',
      tagIds: [],
      userId: null,
      showInactiveUsersOnly: false,
    };
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
    onClose?.();
  };

  const hasActiveFilters = () => {
    return (
      localFilters.hidePastSchedules ||
      localFilters.shiftTypeId !== null ||
      (localFilters.dateRange && localFilters.dateRange.start && localFilters.dateRange.end) ||
      localFilters.status !== 'all' ||
      (localFilters.tagIds && localFilters.tagIds.length > 0) ||
      localFilters.userId !== null ||
      localFilters.showInactiveUsersOnly
    );
  };

  // Filter users to only show active users for the user selection
  const activeUsers = users.filter(user => user.is_active);

  const applyFilters = () => {
    // Only apply date range filter if both dates are set
    const filtersToApply = {
      ...localFilters,
      dateRange: (localFilters.dateRange && localFilters.dateRange.start && localFilters.dateRange.end) 
        ? localFilters.dateRange 
        : null
    };
    onFiltersChange(filtersToApply);
    onClose?.();
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterList />
            <Typography level="h4">Filters</Typography>
            {hasActiveFilters() && (
              <Chip size="sm" color="primary" variant="soft">
                Active
              </Chip>
            )}
          </Stack>

          <Stack spacing={2}>
            {/* Hide Past Schedules */}
            <FormControl>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormLabel>Hide Past Schedules</FormLabel>
                <Switch
                  checked={localFilters.hidePastSchedules}
                  onChange={(e) => handleFilterChange('hidePastSchedules', e.target.checked)}
                />
              </Stack>
            </FormControl>

            <Divider />

            {/* Shift Type Filter */}
            <FormControl>
              <FormLabel>Shift Type</FormLabel>
              <Select
                placeholder="All shift types"
                value={localFilters.shiftTypeId ? String(localFilters.shiftTypeId) : ''}
                onChange={(_, value) => handleFilterChange('shiftTypeId', value ? Number(value) : null)}
              >
                <Option value="">All Shift Types</Option>
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={String(shiftType.id)}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                        }}
                      />
                      {shiftType.name}
                    </Stack>
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Date Range Filter */}
            <Stack spacing={1}>
              <Typography level="title-sm">Date Range</Typography>
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <Input
                  type="date"
                  value={localFilters.dateRange?.start || ''}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>End Date</FormLabel>
                <Input
                  type="date"
                  value={localFilters.dateRange?.end || ''}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  slotProps={{
                    input: {
                      min: localFilters.dateRange?.start || undefined,
                    },
                  }}
                />
              </FormControl>
              {localFilters.dateRange?.start && !localFilters.dateRange?.end && (
                <Typography level="body-xs" color="neutral">
                  Select an end date to apply date range filter
                </Typography>
              )}
            </Stack>

            <Divider />

            {/* Status Filter */}
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={localFilters.status}
                onChange={(_, value) => handleFilterChange('status', value || 'all')}
              >
                <Option value="all">All Statuses</Option>
                <Option value="draft">Draft</Option>
                <Option value="published">Published</Option>
                <Option value="updated">Updated</Option>
              </Select>
            </FormControl>

            <Divider />

            {/* User Filter */}
            <FormControl>
              <FormLabel>User</FormLabel>
              <Select
                placeholder="All users"
                value={localFilters.userId ? String(localFilters.userId) : ''}
                onChange={(_, value) => handleFilterChange('userId', value ? Number(value) : null)}
              >
                <Option value="">All Users</Option>
                {activeUsers.map((user) => (
                  <Option key={user.id} value={String(user.id)}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Show Inactive Users Only */}
            <FormControl>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormLabel>Show Inactive Users Only</FormLabel>
                <Switch
                  checked={localFilters.showInactiveUsersOnly}
                  onChange={(e) => handleFilterChange('showInactiveUsersOnly', e.target.checked)}
                />
              </Stack>
            </FormControl>

            <Divider />

            {/* Tags Filter */}
            <FormControl>
              <FormLabel>Tags</FormLabel>
              <Select
                multiple
                placeholder="All tags"
                value={localFilters.tagIds || []}
                onChange={(_, value) => {
                  const selectedIds = Array.isArray(value) 
                    ? value.map(v => Number(v))
                    : value ? [Number(value)] : [];
                  handleFilterChange('tagIds', selectedIds);
                }}
              >
                {tags.map((tag) => (
                  <Option key={tag.id} value={tag.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: tag.color,
                        }}
                      />
                      {tag.name}
                    </Stack>
                  </Option>
                ))}
              </Select>
              {localFilters.tagIds && localFilters.tagIds.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={1}>
                  {localFilters.tagIds.map(tagId => {
                    const tag = tags.find(t => t.id === tagId);
                    return tag ? (
                      <Chip key={tagId} size="sm" variant="soft" sx={{ backgroundColor: `${tag.color}20`, color: tag.color }}>
                        {tag.name}
                      </Chip>
                    ) : null;
                  })}
                </Stack>
              )}
            </FormControl>
          </Stack>

          <Divider />

          {/* Action Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              startDecorator={<FilterList />}
              onClick={applyFilters}
              size="sm"
            >
              Apply Filters
            </Button>
            <Button
              startDecorator={<Clear />}
              onClick={clearFilters}
              size="sm"
              variant="outlined"
              color="neutral"
            >
              Clear All
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
} 